import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const DAY_NAMES = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];

const EMPTY_SLOT = { start_time: "", end_time: "", max_people: 10 };

export default function ReservationRulesForm({ authAxios, onSaved, editingRule = null, onCancel = null }) {
  const [dayOfWeek, setDayOfWeek] = useState("");
  const [label, setLabel] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [slots, setSlots] = useState([{ ...EMPTY_SLOT }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingRule) {
      setDayOfWeek(String(editingRule.day_of_week));
      setLabel(editingRule.label || DAY_NAMES[editingRule.day_of_week]);
      setEnabled(editingRule.enabled);
      setSlots(editingRule.slots && editingRule.slots.length > 0 ? editingRule.slots : [{ ...EMPTY_SLOT }]);
    }
  }, [editingRule]);

  const addSlot = () => setSlots(prev => [...prev, { ...EMPTY_SLOT }]);

  const removeSlot = (i) => setSlots(prev => prev.filter((_, idx) => idx !== i));

  const updateSlot = (i, field, value) =>
    setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const validate = () => {
    if (dayOfWeek === "") { toast.error("Seleciona um dia da semana"); return false; }
    if (slots.length === 0) { toast.error("Adiciona pelo menos um bloco de horário"); return false; }
    for (const s of slots) {
      if (!s.start_time || !s.end_time) { toast.error("Preenche hora inicial e final de todos os blocos"); return false; }
      if (!s.max_people || Number(s.max_people) < 1) { toast.error("Capacidade deve ser >= 1 pessoa"); return false; }
      if (s.start_time >= s.end_time) { toast.error("Hora inicial deve ser antes da hora final"); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        day_of_week: Number(dayOfWeek),
        label: label || DAY_NAMES[Number(dayOfWeek)],
        enabled,
        slots: slots.map(s => ({ ...s, max_people: Number(s.max_people) })),
      };

      if (editingRule) {
        await authAxios.patch(`/reservation-rules/${editingRule.id}`, {
          label: payload.label,
          enabled: payload.enabled,
          slots: payload.slots,
        });
        toast.success("Regra actualizada com sucesso!");
      } else {
        await authAxios.post("/reservation-rules", payload);
        toast.success("Regra guardada com sucesso!");
      }

      onSaved?.();
      setSlots([{ ...EMPTY_SLOT }]);
      setLabel("");
      setDayOfWeek("");
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erro ao guardar regra");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      {/* Day + Label + Enabled */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-semibold">Dia da semana *</Label>
          <Select value={String(dayOfWeek)} onValueChange={setDayOfWeek}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar dia" /></SelectTrigger>
            <SelectContent>
              {DAY_NAMES.map((d, i) => (
                <SelectItem key={i} value={String(i)}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Nome da regra</Label>
          <Input
            className="mt-1"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder={dayOfWeek !== "" ? DAY_NAMES[Number(dayOfWeek)] : "Ex: Rodízio de quarta"}
          />
        </div>
        <div className="flex flex-col justify-end">
          <Label className="text-xs font-semibold mb-2">Activa</Label>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-sm text-gray-600">{enabled ? "Sim" : "Não"}</span>
          </div>
        </div>
      </div>

      {/* Slot list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-semibold">Blocos de horário</Label>
          <Button variant="outline" size="sm" onClick={addSlot} className="flex items-center gap-1 text-xs">
            <Plus className="w-3.5 h-3.5" /> Adicionar bloco
          </Button>
        </div>

        <div className="space-y-2">
          {slots.map((slot, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end bg-gray-50 p-3 rounded-lg border">
              <div>
                <Label className="text-[10px] text-gray-500">Hora inicial</Label>
                <Input
                  type="time"
                  value={slot.start_time}
                  onChange={e => updateSlot(i, "start_time", e.target.value)}
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Hora final</Label>
                <Input
                  type="time"
                  value={slot.end_time}
                  onChange={e => updateSlot(i, "end_time", e.target.value)}
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Máx. pessoas</Label>
                <Input
                  type="number"
                  min={1}
                  max={999}
                  value={slot.max_people}
                  onChange={e => updateSlot(i, "max_people", e.target.value)}
                  className="mt-1 text-sm"
                />
              </div>
              <button
                onClick={() => removeSlot(i)}
                disabled={slots.length === 1}
                className="p-2 text-red-400 hover:text-red-600 disabled:opacity-30 transition"
                title="Remover bloco"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        {editingRule && (
          <Button onClick={onCancel} disabled={saving} variant="outline" className="text-gray-700">
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {editingRule ? "Actualizar regra" : "Guardar regra"}
        </Button>
      </div>
    </div>
  );
}
